import { z } from "zod";

/**
 * The company's opening hours (f4-08): shifts per weekday (several for split shifts),
 * special days (holidays, closures, other hours) and "open 24 hours". Times are
 * Costa Rica's ("08:00", "17:30"); week[0] is Sunday, as Date.getDay().
 */

const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/, "HH:MM");
const shiftSchema = z
  .object({ from: time, to: time })
  .refine((s) => s.from < s.to, { message: "A shift ends after it starts" });

export const scheduleSchema = z.object({
  alwaysOpen: z.boolean().default(false),
  week: z.array(z.array(shiftSchema).max(4)).length(7),
  special: z
    .array(
      z.object({
        date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        label: z.string().trim().max(60).optional(),
        /** Empty: closed that day. */
        shifts: z.array(shiftSchema).max(4).default([]),
      }),
    )
    .max(60)
    .default([]),
});
export type Schedule = z.infer<typeof scheduleSchema>;
export type Shift = Schedule["week"][number][number];

const CR_OFFSET_MS = -6 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_NAMES = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
/** Monday first, as people read a week. */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** "8 a. m.", "5:30 p. m.", "12 m.d." (noon), as written in Costa Rica. */
export function timeText(hhmm: string): string {
  const [h = 0, m = 0] = hhmm.split(":").map(Number);
  if (h === 24 || (h === 0 && m === 0)) return "12 de la noche";
  if (h === 12 && m === 0) return "12 m.d.";
  const hour = h % 12 || 12;
  return `${hour}${m ? `:${String(m).padStart(2, "0")}` : ""} ${h < 12 ? "a. m." : "p. m."}`;
}

const shiftsText = (shifts: Shift[]) =>
  shifts.length ? shifts.map((s) => `${timeText(s.from)} a ${timeText(s.to)}`).join(" y ") : "cerrado";

/**
 * The week in words, for {{horario}} and for Lumi: consecutive days with the same hours
 * go together ("lunes a viernes de 8 a. m. a 5 p. m.; sábado de 8 a. m. a 12 m.d.").
 */
export function scheduleText(s: Schedule): string {
  if (s.alwaysOpen) return "Abierto las 24 horas, todos los días";
  const groups: { days: number[]; text: string }[] = [];
  for (const day of WEEK_ORDER) {
    const text = shiftsText(s.week[day] ?? []);
    const last = groups.at(-1);
    if (last && last.text === text) last.days.push(day);
    else groups.push({ days: [day], text });
  }
  const parts = groups.map((g) => {
    const first = DAY_NAMES[g.days[0] as number];
    const last = DAY_NAMES[g.days.at(-1) as number];
    const days = g.days.length === 1 ? first : g.days.length === 2 ? `${first} y ${last}` : `${first} a ${last}`;
    return g.text === "cerrado" ? `${days}: cerrado` : `${days} de ${g.text}`;
  });
  const text = parts.join("; ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Costa Rica's calendar date and minutes since midnight for an instant. */
function local(at: Date) {
  const d = new Date(at.getTime() + CR_OFFSET_MS);
  const date = d.toISOString().slice(0, 10);
  return { date, day: d.getUTCDay(), minutes: d.getUTCHours() * 60 + d.getUTCMinutes() };
}

const toMinutes = (hhmm: string) => {
  const [h = 0, m = 0] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

function shiftsOn(s: Schedule, date: string, day: number): Shift[] {
  const special = s.special.find((x) => x.date === date);
  return special ? special.shifts : (s.week[day] ?? []);
}

export interface OpenState {
  open: boolean;
  /** When it closes today, if open. */
  closesAt?: string;
  /** When it opens next, if closed: "hoy a las 2 p. m.", "mañana a las 8 a. m.", "el lunes a las 8 a. m.". */
  opensText?: string;
  /** The special day that applies today, if any ("Feriado", "Inventario"). */
  specialLabel?: string;
}

/** Whether the company is open now (Costa Rica time), and when it opens next. */
export function openState(s: Schedule, now = new Date()): OpenState {
  if (s.alwaysOpen) return { open: true };
  const today = local(now);
  const special = s.special.find((x) => x.date === today.date);
  const current = shiftsOn(s, today.date, today.day).find(
    (sh) => toMinutes(sh.from) <= today.minutes && today.minutes < toMinutes(sh.to),
  );
  if (current) return { open: true, closesAt: timeText(current.to), specialLabel: special?.label };
  for (let ahead = 0; ahead < 14; ahead++) {
    const at = new Date(now.getTime() + ahead * DAY_MS);
    const { date, day } = local(at);
    const next = shiftsOn(s, date, day)
      .filter((sh) => ahead > 0 || toMinutes(sh.from) > today.minutes)
      .sort((a, b) => a.from.localeCompare(b.from))[0];
    if (!next) continue;
    const when = ahead === 0 ? "hoy" : ahead === 1 ? "mañana" : `el ${DAY_NAMES[day]}`;
    return { open: false, opensText: `${when} a las ${timeText(next.from)}`, specialLabel: special?.label };
  }
  return { open: false, specialLabel: special?.label };
}

/** Ends a sentence without doubling the period of "8 a. m.". */
export const sentence = (text: string) => (/[.!?]$/.test(text) ? text : `${text}.`);

/** An empty week: everything closed until the company fills it in. */
export const EMPTY_SCHEDULE: Schedule = { alwaysOpen: false, week: [[], [], [], [], [], [], []], special: [] };

/** "le respondemos" (usted) or "te respondemos" (tú, vos). */
const TO_YOU = { usted: "le", tu: "te", vos: "te" } as const;
export type Address = keyof typeof TO_YOU;

/**
 * The fixed handoff notice, plus when a person answers if the company is closed now:
 * "Le paso con un asesor… Estamos fuera de horario: le respondemos mañana a las 8 a. m."
 */
export function handoffNotice(
  message: string,
  schedule: Schedule | null | undefined,
  now = new Date(),
  address: Address = "usted",
): string {
  if (!schedule) return message;
  const state = openState(schedule, now);
  if (state.open || !state.opensText) return message;
  return `${message} Estamos fuera de horario: ${TO_YOU[address]} respondemos ${sentence(state.opensText)}`;
}

/** Whether the company is closed right now (no hours set: never). */
export const closedNow = (schedule: Schedule | null | undefined, now = new Date()) =>
  !!schedule && !openState(schedule, now).open;

/**
 * The message sent instead of Lumi outside the hours: the company's own, or Orbis's in
 * its treatment. {apertura} becomes when it opens ("mañana a las 8 a. m.").
 */
export function offHoursText(
  custom: string | null | undefined,
  schedule: Schedule | null | undefined,
  address: Address = "usted",
  now = new Date(),
): string {
  const opens = schedule ? openState(schedule, now).opensText : undefined;
  const text =
    custom?.trim() ||
    `¡Gracias por escribir! En este momento estamos cerrados: ${TO_YOU[address]} respondemos {apertura}.`;
  return text.replaceAll("{apertura}", opens ?? "apenas abramos").replace(/\.\.$/, ".");
}
